<template>
  <div class="operator-record-container">
    <!-- 記錄列表 -->
      <v-card>
        <v-card-title class="pb-2">
          <span class="subtitle-1">操作記錄</span>
          <v-spacer></v-spacer>
          <div class="d-flex align-center" style="gap: 12px;">
            <v-chip small color="info" outlined>
              總計：{{ totalElements }} 筆
            </v-chip>
            <v-sheet width="150">
              <v-autocomplete
                v-model="selected_org"
                solo
                dense
                hide-details
                color="primary"
                prepend-inner-icon="mdi-filter"
                :items="org_list"
                :filter="() => true"
                @change="selectedOrg()"
                @update:search-input="searchOrgSelector"
              />
            </v-sheet>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <!-- 載入狀態 -->
        <v-card v-if="loading" class="pa-4">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-avatar-three-line"
            class="mb-2"
          ></v-skeleton-loader>
        </v-card>

        <v-card-text v-else class="pa-0">
        <!-- 沒有資料時的顯示 -->
        <div v-if="!records || records.length === 0" class="text-center pa-8">
          <v-icon size="64" color="grey lighten-2">mdi-inbox</v-icon>
          <div class="grey--text mt-2">目前沒有操作記錄</div>
        </div>

        <!-- 記錄項目 -->
        <template v-else>
          <v-list>
            <template v-for="(record, index) in records">
              <v-list-item :key="record.id" class="py-3">
                <v-list-item-avatar>
                  <v-avatar color="primary" size="40">
                    <v-icon dark>mdi-account-cog</v-icon>
                  </v-avatar>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="mb-1">
                    <v-row no-gutters align="center">
                      <v-col>
                        <span class="font-weight-medium">操作人: {{ getMemoData(record.memo).admin_name }}</span>
                        <v-chip small class="ml-2" color="success" outlined>
                          {{ formatAction(record.action) }}
                        </v-chip>
                      </v-col>
                      <v-col cols="auto">
                      <span class="caption grey--text">
                        {{ formatDate(record.created_at) }}
                      </span>
                      </v-col>
                    </v-row>
                  </v-list-item-title>

                  <v-list-item-subtitle class="mb-2">
                    <div class="body-2 mb-1">
                      <strong>組織：</strong>{{ getMemoData(record.memo).org_name }}
                    </div>
                    <div class="body-2 mb-1">
                      <strong>方案：</strong>{{ getMemoData(record.memo).item_name }}
                    </div>
                    <div class="body-2 mb-1">
                      <strong>數量：</strong>{{ getMemoData(record.memo).req.quantity}}
                    </div>

                  </v-list-item-subtitle>

                  <!-- 詳細資訊卡片 -->
                  <v-card flat color="grey lighten-5" class="mt-2">
                    <v-card-text class="py-2">
                      <v-row dense>
                        <v-col cols="12" md="4">
                          <div class="caption grey--text">狀態</div>
                          <v-chip
                              small
                              :color="getMemoData(record.memo).ok === 1 ? 'success' : 'error'"
                              dark
                          >
                            {{ getMemoData(record.memo).ok === 1 ? '成功' : '失敗' }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-list-item-content>
              </v-list-item>

              <v-divider v-if="index < records.length - 1" :key="`divider-${record.id}`"></v-divider>
            </template>
          </v-list>
        </template>
        </v-card-text>
    </v-card>

    <!-- 分頁元件 -->
    <div class="text-center mt-5">
      <Pagination
        v-if="page_setting.is_show"
        :setting="page_setting"
      />
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Utils/Pagination.vue'
import { apiGetOperatorRecord, apiGetOrganization } from '@/api/admin.js'

export default {
  name: 'OperatorRecord',
  components: {
    Pagination
  },
  data() {
    return {
      loading: false,
      records: [],
      totalElements: 0,
      page_setting: {
        is_show: false,
        selected_page: 1,
        total_page: 1,
        total_visible: 10,
      },
      apiParams: {
        action: "admin_addSubscription",
        page: 1,
        limit: 10
      },
      action_map: {
        'admin_addSubscription': '贈送訂閱'
      },
      is_change_select: false,
      selected_org: null,
      org_list: [{ text: '全部', value: '' }],
      search_org_timer: null,
    }
  },
  mounted() {
    // 從網址 query 拿 page，沒有就預設 1
    const page_from_query = parseInt(this.$route.query.page, 10) || 1
    this.apiParams.page = page_from_query
    this.page_setting.selected_page = page_from_query
    this.loadRecords()
    this.getOrgList()
    this.selected_org = ''
  },
  methods: {
    async loadRecords() {
      this.loading = true
      try {
        this.apiParams.org_id = this.selected_org
        const response = await apiGetOperatorRecord(this.apiParams)
        
        if (response.ok === 1) {
          this.records = response.data.content || []
          this.totalElements = response.data.totalElements || 0
          
          // 設定分頁
          this.page_setting.is_show = this.totalElements > 0
          this.page_setting.total_page = response.data.totalPages || 1
          this.page_setting.selected_page = (response.data.number || 0) + 1 // API回傳的是0-based
        }
      } catch (error) {
        console.error('載入記錄失敗:', error)
        this.$root.vtoast.show({
          message: '載入記錄失敗，請稍後再試'
        })
      } finally {
        this.loading = false
      }
    },
    async getOrgList(keyword, limit = 30) {
      try {
        const params = {
          key: keyword,
          limit,
        }
        const res = await apiGetOrganization(params)
        if (res.code !== 200) throw new Error('取得組織清單失敗')
        const list = res.data.content.map((item) => ({
          text: item.org_name,
          value: item.org_id,
        }))
        this.org_list = [{ text: '全部', value: '' }, ...list]
      } catch (error) {
        console.error('getOrgList() error :', error)
      }
    },
    getMemoData(memoString) {
      try {
        return JSON.parse(memoString)
      } catch (error) {
        console.error('解析memo失敗:', error)
        return {
          org_name: '未知',
          admin_name: '未知',
          admin_email: '未知',
          item_name: '未知',
          level: 0,
          duration: 0,
          ok: 0,
          req: {}
        }
      }
    },
    formatDate(dateString) {
      try {
        const date = new Date(dateString)
        return date.toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },
    formatAction(action) {
        let format_action = action
        if (this.action_map[format_action]) {
            format_action = this.action_map[format_action]
        }
        return format_action
    },
    handlePageChange(newPage) {
      this.apiParams.page = newPage
      this.page_setting.selected_page = newPage
      this.loadRecords()
    },
    selectedOrg() {
      this.is_change_select = true
      this.loadRecords()
    },
    searchOrgSelector(value) {
      if (this.is_change_select) {
        return this.is_change_select = false
      }
      const keyword = value === '全部' ? null : value
      if (this.search_org_timer) {
        clearTimeout(this.search_org_timer)
      }
      this.search_org_timer = setTimeout(() => {
        this.getOrgList(keyword, keyword ? 5 : 30)
      }, 500)
    },
  }
}
</script>

<style scoped>
.operator-record-container {
  padding: 16px;
}

pre {
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 8px 0;
}
</style>